/**
 * 공통 스타일 유틸리티 함수들
 * 다른 컴포넌트에서 공통 스타일을 쉽게 사용할 수 있도록 도와줍니다.
 */

import commonStyles from '../styles/CommonStyles.module.css';

/**
 * 페이지 컨테이너 스타일을 반환합니다.
 * @returns {Object} 페이지 컨테이너 관련 스타일 객체
 */
export const getPageContainerStyles = () => ({
  pageContainer: commonStyles.pageContainer,
  pageScrollContainer: commonStyles.pageScrollContainer,
  pageScrollSafeArea: commonStyles.pageScrollSafeArea,
  pageTitle: commonStyles.pageTitle,
});

/**
 * 검색 폼 스타일을 반환합니다.
 * @returns {Object} 검색 폼 관련 스타일 객체
 */
export const getSearchFormStyles = () => ({
  searchForm: commonStyles.searchForm,
  searchSelect: commonStyles.searchForm,
  searchInput: commonStyles.searchForm,
  searchButton: commonStyles.searchForm,
});

/**
 * 필터 버튼 스타일을 반환합니다.
 * @returns {Object} 필터 버튼 관련 스타일 객체
 */
export const getFilterStyles = () => ({
  statusFilter: commonStyles.statusFilter,
  statusButton: commonStyles.statusButton,
  selected: commonStyles.selected,
});

/**
 * 상태 필터 스타일을 반환합니다.
 * @returns {Object} 상태 필터 관련 스타일 객체
 */
export const getStatusFilterStyles = () => ({
  statusFilter: commonStyles.statusFilter,
  statusButton: commonStyles.statusButton,
  selected: commonStyles.selected,
});

/**
 * 로딩 스타일을 반환합니다.
 * @returns {Object} 로딩 관련 스타일 객체
 */
export const getLoadingStyles = () => ({
  loadingCell: commonStyles.loadingCell,
  emptyCell: commonStyles.emptyCell,
});

/**
 * 날짜 풍선 스타일을 반환합니다.
 * @returns {Object} 날짜 풍선 관련 스타일 객체
 */
export const getDateTagStyles = () => ({
  dateTagsContainer: commonStyles.dateTagsContainer,
  dateTag: commonStyles.dateTag,
  dateTagCount: commonStyles.dateTagCount,
});

/**
 * 모달 내부 스타일을 반환합니다.
 * @returns {Object} 모달 내부 관련 스타일 객체
 */
export const getModalContentStyles = () => ({
  modalInfoSection: commonStyles.modalInfoSection,
  modalInfoRow: commonStyles.modalInfoRow,
  modalInfoItem: commonStyles.modalInfoItem,
  modalInfoLabel: commonStyles.modalInfoLabel,
  modalInfoValue: commonStyles.modalInfoValue,
  modalInfoBlock: commonStyles.modalInfoBlock,
  modalInfoBlockLabel: commonStyles.modalInfoBlockLabel,
  modalInfoBlockContent: commonStyles.modalInfoBlockContent,
  modalImageGrid: commonStyles.modalImageGrid,
  modalImage: commonStyles.modalImage,
  modalTextarea: commonStyles.modalTextarea,
  modalTextareaNote: commonStyles.modalTextareaNote,
  warningText: commonStyles.warningText,
  threeColumnGrid: commonStyles.threeColumnGrid,
  inputGroup: commonStyles.inputGroup,
  inputLabel: commonStyles.inputLabel,
  inputWithDesc: commonStyles.inputWithDesc,
  inputDesc: commonStyles.inputDesc,
  amountValue: commonStyles.amountValue,
  dateInfo: commonStyles.dateInfo,
  personalDetails: commonStyles.personalDetails,
  referenceImage: commonStyles.referenceImage,
  dateInfoSection: commonStyles.dateInfoSection,
  dateRow: commonStyles.dateRow,
  dateLabel: commonStyles.dateLabel,
  dateValue: commonStyles.dateValue,
});

/**
 * 테이블 스타일을 반환합니다.
 * @returns {Object} 테이블 관련 스타일 객체
 */
export const getTableStyles = () => ({
  tableContainer: commonStyles.tableContainer,
  table: commonStyles.table,
  tableHeader: commonStyles.tableHeader,
  tableBody: commonStyles.tableBody,
  tableRow: commonStyles.tableRow,
  statusCell: commonStyles.statusCell,
});

/**
 * 상태별 스타일을 반환합니다.
 * @returns {Object} 상태별 스타일 객체
 */
export const getStatusStyles = () => ({
  statusCell: commonStyles.statusCell,
  statusRequested: commonStyles.statusRequested,
  statusInProgress: commonStyles.statusInProgress,
  statusPending: commonStyles.statusPending,
  statusConfirmed: commonStyles.statusConfirmed,
  statusDepositPaid: commonStyles.statusDepositPaid,
  statusDepositConfirmed: commonStyles.statusDepositConfirmed,
  statusCompleted: commonStyles.statusCompleted,
  statusCancelled: commonStyles.statusCancelled,
  confirmedStatus: commonStyles.confirmedStatus,
});

/**
 * 모달 내부 상태별 스타일을 반환합니다.
 * @returns {Object} 모달 내부 상태별 스타일 객체
 */
export const getModalStatusStyles = () => ({
  modalStatusRequested: commonStyles.modalStatusRequested,
  modalStatusInProgress: commonStyles.modalStatusInProgress,
  modalStatusConfirmed: commonStyles.modalStatusConfirmed,
  modalStatusDepositPaid: commonStyles.modalStatusDepositPaid,
  modalStatusCompleted: commonStyles.modalStatusCompleted,
  modalStatusCancelled: commonStyles.modalStatusCancelled,
});

/**
 * 모달 스타일을 반환합니다.
 * @returns {Object} 모달 관련 스타일 객체
 */
export const getModalStyles = () => ({
  modalOverlay: commonStyles.modalOverlay,
  modal: commonStyles.modal,
  modalHeader: commonStyles.modalHeader,
  modalContent: commonStyles.modalContent,
  closeButton: commonStyles.closeButton,
});

/**
 * 탭 스타일을 반환합니다.
 * @returns {Object} 탭 관련 스타일 객체
 */
export const getTabStyles = () => ({
  tabButtons: commonStyles.tabButtons,
  tabButton: commonStyles.tabButton,
  activeTab: commonStyles.activeTab,
});

/**
 * 모달 액션 버튼 스타일을 반환합니다.
 * @returns {Object} 모달 액션 버튼 관련 스타일 객체
 */
export const getModalActionStyles = () => ({
  modalActions: commonStyles.modalActions,
  actionButton: commonStyles.actionButton,
  deleteButton: commonStyles.deleteButton,
  secondaryButton: commonStyles.secondaryButton,
  gradientPinkButton: commonStyles.gradientPinkButton,
  saveSuccess: commonStyles.saveSuccess,
});

/**
 * 메시지 모달 스타일을 반환합니다.
 * @returns {Object} 메시지 모달 관련 스타일 객체
 */
export const getMessageModalStyles = () => ({
  messageModal: commonStyles.messageModal,
  messageModalOverlay: commonStyles.messageModalOverlay,
  messageInput: commonStyles.messageInput,
});

export const getDateTimeInputStyles = () => ({
  dateTimeInput: commonStyles.dateTimeInput,
});

export const getCommonInputStyles = () => ({
  commonInput: commonStyles.commonInput,
});

/**
 * 모든 공통 스타일을 반환합니다.
 * @returns {Object} 모든 공통 스타일 객체
 */
export const getAllCommonStyles = () => ({
  ...getPageContainerStyles(),
  ...getSearchFormStyles(),
  ...getFilterStyles(),
  ...getTableStyles(),
  ...getStatusStyles(),
  ...getModalStyles(),
  ...getTabStyles(),
  ...getModalActionStyles(),
  ...getMessageModalStyles(),
  ...getDateTimeInputStyles(),
  ...getCommonInputStyles(),
});

/**
 * 상태에 따른 CSS 클래스를 반환합니다.
 * @param {string} status - 상태값
 * @returns {string} 해당 상태의 CSS 클래스명
 */
export const getStatusClass = (status) => {
  const statusStyles = getStatusStyles();
  switch (status) {
    case 'requested': return statusStyles.statusRequested;
    case 'in_progress': return statusStyles.statusInProgress;
    case 'confirmed': return statusStyles.statusConfirmed;
    case 'deposit_paid': return statusStyles.statusDepositPaid;
    case 'deposit_confirmed': return statusStyles.statusDepositConfirmed;
    case 'completed': return statusStyles.statusCompleted;
    case 'cancelled': return statusStyles.statusCancelled;
    default: return '';
  }
};

/**
 * 모달 내부 상태에 따른 CSS 클래스를 반환합니다.
 * @param {string} status - 상태값
 * @returns {string} 해당 상태의 모달 CSS 클래스명
 */
export const getModalStatusClass = (status) => {
  const modalStatusStyles = getModalStatusStyles();
  switch (status) {
    case 'requested': return modalStatusStyles.modalStatusRequested;
    case 'in_progress': return modalStatusStyles.modalStatusInProgress;
    case 'confirmed': return modalStatusStyles.modalStatusConfirmed;
    case 'deposit_paid': return modalStatusStyles.modalStatusDepositPaid;
    case 'completed': return modalStatusStyles.modalStatusCompleted;
    case 'cancelled': return modalStatusStyles.modalStatusCancelled;
    default: return '';
  }
};

/**
 * 버튼 변형에 따른 CSS 클래스를 반환합니다.
 * @param {string} variant - 버튼 변형 ('default', 'delete', 'gradientPink')
 * @returns {string} 해당 변형의 CSS 클래스명
 */
export const getButtonVariantClass = (variant) => {
  const actionStyles = getModalActionStyles();
  switch (variant) {
    case 'delete': return actionStyles.deleteButton;
    case 'gradientPink': return actionStyles.gradientPinkButton;
    default: return actionStyles.actionButton;
  }
};
